// src/hooks/useCompanyGroTemplates.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createCompanyGroActionPlan,
  createCompanyGroActionPlanItem,
  createCompanyGroDimension,
  createCompanyGroDimensionRiskRule,
  createCompanyGroRisk,
  createCompanyGroRiskActionLink,
  createCompanyGroRuleActionLink,
  deleteCompanyGroRiskActionLink,
  deleteCompanyGroRuleActionLink,
  getCompanyGroTemplates,
  setCompanyGroActionPlanItemStatus,
  setCompanyGroActionPlanStatus,
  setCompanyGroDimensionRiskRuleStatus,
  setCompanyGroDimensionStatus,
  setCompanyGroRiskStatus,
  updateCompanyGroActionPlan,
  updateCompanyGroActionPlanItem,
  updateCompanyGroDimension,
  updateCompanyGroDimensionRiskRule,
  updateCompanyGroRisk,
  type CompanyGroActionPlanInput,
  type CompanyGroActionPlanItemInput,
  type CompanyGroDimensionInput,
  type CompanyGroDimensionRiskRuleInput,
  type CompanyGroRiskInput,
} from "@/services/modelImports";

const QUERY_KEY = ["company_gro_templates"] as const;

export function useCompanyGroTemplates() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: getCompanyGroTemplates,
    staleTime: 15_000,
  });
}

function useRefreshMutation<TVariables, TData>(mutationFn: (variables: TVariables) => Promise<TData>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useSaveCompanyGroRisk() {
  return useRefreshMutation(({ id, body }: { id?: string; body: CompanyGroRiskInput }) =>
    id ? updateCompanyGroRisk(id, body) : createCompanyGroRisk(body),
  );
}

export function useSetCompanyGroRiskStatus() {
  return useRefreshMutation(({ id, is_active }: { id: string; is_active: boolean }) =>
    setCompanyGroRiskStatus(id, is_active),
  );
}

export function useSaveCompanyGroDimension() {
  return useRefreshMutation(({ id, body }: { id?: string; body: CompanyGroDimensionInput }) =>
    id ? updateCompanyGroDimension(id, body) : createCompanyGroDimension(body),
  );
}

export function useSetCompanyGroDimensionStatus() {
  return useRefreshMutation(({ id, is_active }: { id: string; is_active: boolean }) =>
    setCompanyGroDimensionStatus(id, is_active),
  );
}

export function useSaveCompanyGroRule() {
  return useRefreshMutation(({ id, body }: { id?: string; body: CompanyGroDimensionRiskRuleInput }) =>
    id ? updateCompanyGroDimensionRiskRule(id, body) : createCompanyGroDimensionRiskRule(body),
  );
}

export function useSetCompanyGroRuleStatus() {
  return useRefreshMutation(({ id, is_active }: { id: string; is_active: boolean }) =>
    setCompanyGroDimensionRiskRuleStatus(id, is_active),
  );
}

export function useSaveCompanyGroActionPlan() {
  return useRefreshMutation(({ id, body }: { id?: string; body: CompanyGroActionPlanInput }) =>
    id ? updateCompanyGroActionPlan(id, body) : createCompanyGroActionPlan(body),
  );
}

export function useSetCompanyGroActionPlanStatus() {
  return useRefreshMutation(({ id, is_active }: { id: string; is_active: boolean }) =>
    setCompanyGroActionPlanStatus(id, is_active),
  );
}

export function useSaveCompanyGroActionPlanItem() {
  return useRefreshMutation(
    ({ planId, itemId, body }: { planId: string; itemId?: string; body: CompanyGroActionPlanItemInput }) =>
      itemId ? updateCompanyGroActionPlanItem(itemId, body) : createCompanyGroActionPlanItem(planId, body),
  );
}

export function useSetCompanyGroActionPlanItemStatus() {
  return useRefreshMutation(({ id, is_active }: { id: string; is_active: boolean }) =>
    setCompanyGroActionPlanItemStatus(id, is_active),
  );
}

export function useCreateCompanyGroRiskActionLink() {
  return useRefreshMutation(({ riskId, planId }: { riskId: string; planId: string }) =>
    createCompanyGroRiskActionLink(riskId, planId),
  );
}

export function useDeleteCompanyGroRiskActionLink() {
  return useRefreshMutation((id: string) => deleteCompanyGroRiskActionLink(id));
}

export function useCreateCompanyGroRuleActionLink() {
  return useRefreshMutation(
    ({ ruleId, planId, sortOrder, isDefault }: { ruleId: string; planId: string; sortOrder?: number; isDefault?: boolean }) =>
      createCompanyGroRuleActionLink(ruleId, planId, sortOrder, isDefault),
  );
}

export function useDeleteCompanyGroRuleActionLink() {
  return useRefreshMutation((id: string) => deleteCompanyGroRuleActionLink(id));
}
